// packages/world-model/src/traits/switchable/switchableTrait.ts

import { ITrait } from '../trait.js';
import { TraitType } from '../trait-types.js';

export interface ISwitchableData {
  /** Whether the entity is currently on */
  isOn?: boolean;
  
  /** Whether the entity starts on */
  startsOn?: boolean;
  
  /** Power consumption when on (arbitrary units) */
  powerConsumption?: number;
  
  /** Whether this requires power to operate */
  requiresPower?: boolean;
  
  /** Whether power is currently available (a story's own flag; ignored when power sources are declared) */
  hasPower?: boolean;

  /**
   * The things that power it (ADR-374 D10): it has power exactly while every
   * one of them is directly inside it. Declaring any implies `requiresPower`.
   */
  powerSourceIds?: string[];
  
  /** Custom message when turning on */
  onMessage?: string;
  
  /** Custom message when turning off */
  offMessage?: string;
  
  /** Custom message when already on */
  alreadyOnMessage?: string;
  
  /** Custom message when already off */
  alreadyOffMessage?: string;
  
  /** Custom message when no power available */
  noPowerMessage?: string;
  
  /** Sound made when switching on */
  onSound?: string;
  
  /** Sound made when switching off */
  offSound?: string;
  
  /** Continuous sound while on */
  runningSound?: string;
  
  /** Time in turns before auto-off (0 = never) */
  autoOffTime?: number;
  
  /** Turns remaining before auto-off */
  autoOffCounter?: number;

  /** Description when switched on (used by computed description getter on IFEntity) */
  onDescription?: string;

  /** Description when switched off (used by computed description getter on IFEntity) */
  offDescription?: string;

  /**
   * State detail appended to the base description when switched on (ADR-195 S2).
   * Unlike `onDescription` (which *replaces* the description via the computed
   * getter), this is *appended* by the examine `{slot:detail}` channel — the base
   * description stays and the state text follows it. Read by the `state-clauses`
   * registry.
   */
  detailWhenOn?: string;
}

/**
 * Switchable trait for entities that can be turned on and off.
 * Used for lights, machines, devices, etc.
 *
 * This trait contains only data - all switching logic
 * is in SwitchableBehavior.
 */
export class SwitchableTrait implements ITrait, ISwitchableData {
  static readonly type = TraitType.SWITCHABLE;
  readonly type = TraitType.SWITCHABLE;

  // SwitchableData properties
  isOn: boolean;
  startsOn: boolean;
  powerConsumption: number;
  requiresPower: boolean;
  hasPower: boolean;
  powerSourceIds?: string[];
  onMessage?: string;
  offMessage?: string;
  alreadyOnMessage?: string;
  alreadyOffMessage?: string;
  noPowerMessage?: string;
  onSound?: string;
  offSound?: string;
  runningSound?: string;
  autoOffTime: number;
  autoOffCounter: number;
  onDescription?: string;
  offDescription?: string;
  detailWhenOn?: string;

  constructor(data: ISwitchableData = {}) {
    // Set defaults and merge with provided data
    this.startsOn = data.startsOn ?? false;
    this.requiresPower = data.requiresPower ?? false;
    this.hasPower = data.hasPower ?? true;
    if (data.powerSourceIds && data.powerSourceIds.length > 0) {
      this.powerSourceIds = [...data.powerSourceIds];
      this.requiresPower = true;
    }
    this.isOn = data.isOn ?? (this.startsOn && this.hasPower);
    this.powerConsumption = data.powerConsumption ?? 1;
    this.onMessage = data.onMessage;
    this.offMessage = data.offMessage;
    this.alreadyOnMessage = data.alreadyOnMessage;
    this.alreadyOffMessage = data.alreadyOffMessage;
    this.noPowerMessage = data.noPowerMessage;
    this.onSound = data.onSound;
    this.offSound = data.offSound;
    this.runningSound = data.runningSound;
    this.autoOffTime = data.autoOffTime ?? 0;
    this.autoOffCounter = data.autoOffCounter ?? (this.isOn && this.autoOffTime > 0 ? this.autoOffTime : 0);
    this.onDescription = data.onDescription;
    this.offDescription = data.offDescription;
    this.detailWhenOn = data.detailWhenOn;
  }
}
